import React from "react";
import "./App.css";
import "./styles/purge.wordpress.min.css";
import "./styles/globals.css";
import "./styles/skeleton.scss";
import "./styles/faculty.scss";
import "./styles/admin.scss";
import "./styles/custom.scss";
import "./styles/page-hero.scss";

import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { recordVisit } from "./utils/visitorTracking";
import { ToastContainer } from "react-toastify";

import { routes } from "./utils/utils.routes";

import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";

import { Header } from "./components/Header.component";
import { Footer } from "./components/Footer.component";
import { SkipToContent } from "./components/subcomponents/Skipbutton";

// ================= PUBLIC PAGES =================
import { Home } from "./pages/Home";
import { Contacts } from "./pages/Contacts";
import { About } from "./pages/About";
import { Partners } from "./pages/Partners";
import { PartnerDetail } from "./pages/PartnerDetail";
import { Anciens } from "./pages/Anciens";
import { FraisAcademiques } from "./pages/FraisAcademiques";
import { Domaines } from "./pages/Domaines";
import { Articles } from "./pages/Articles";
import { ArticleDerails } from "./pages/Articledetails";
import { Faq } from "./pages/Faq";
import { FaqDetail } from "./pages/FaqDetail";
import { AdmissionGuide } from "./pages/AdmissionGuide";
import { Profile } from "./pages/Profile";
import { Search } from "./pages/Search";
import { NotFound } from "./pages/NotFound";

// ================= AUTH =================
import { Login } from "./pages/Login";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";

// ================= ADMIN =================
import { AdminLayout } from "./components/AdminLayout";
import { AdminDashboard } from "./pages/AdminDashboard";

// ================= OTHER =================
import { Center } from "./pages/CenterDetails";
import { Centers } from "./pages/Centers";
import { Faculties } from "./pages/Faculties";
import { FacultyDetail } from "./pages/FacultyDetail";
import { Library } from "./pages/Bilbiotheques";
import { Metanoia } from "./pages/Metanoia";
import { Kauta } from "./pages/Kauta";
import { SchedulesPage } from "./pages/Schedules";
import { EventsHub } from "./pages/EventsHub";
import { TendersPage } from "./pages/Tenders";
import { SpecialProgramDetail } from "./pages/SpecialProgramDetail";
import { Aumonerie } from "./pages/Aumonerie";
import { IsMobileProvider } from "./contexts/MobileContext";

// ================= PUBLIC LAYOUT =================
const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="site">
    <SkipToContent />
    <Header />
    <main>
      {children}
    </main>
    <Footer />
  </div>
);

const VisitTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/login")) return;
    recordVisit(path);
  }, [location.pathname]);
  return null;
};

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <LanguageProvider>
          <IsMobileProvider>
            <Router>
              <VisitTracker />
              <Routes>
                {/* ================= HOME ================= */}
                <Route path={routes.HOME} element={<PublicLayout><Home /></PublicLayout>} />
                <Route path={routes.HOMEBLANK} element={<PublicLayout><Home /></PublicLayout>} />

                {/* ================= PUBLIC ================= */}
                <Route path={routes.CONTACTS} element={<PublicLayout><Contacts /></PublicLayout>} />
                <Route path={routes.ABOUT} element={<PublicLayout><About /></PublicLayout>} />
                <Route path={routes.PARTNERS} element={<PublicLayout><Partners /></PublicLayout>} />
                <Route path={`${routes.PARTNERS}/:slug`} element={<PublicLayout><PartnerDetail /></PublicLayout>} />
                <Route path={routes.ANCIENS} element={<PublicLayout><Anciens /></PublicLayout>} />
                <Route path={routes.FRAIS} element={<PublicLayout><FraisAcademiques /></PublicLayout>} />
                <Route path={routes.GUIDE_INSCRIPTION} element={<PublicLayout><AdmissionGuide /></PublicLayout>} />
                <Route path={routes.DOMAINES} element={<PublicLayout><Domaines /></PublicLayout>} />
                <Route path={routes.FAQ} element={<PublicLayout><Faq /></PublicLayout>} />
                <Route path={`${routes.FAQ}/:slug`} element={<PublicLayout><FaqDetail /></PublicLayout>} />
                <Route path={`${routes.PROFILE}/:profile`} element={<PublicLayout><Profile /></PublicLayout>} />
                <Route path={routes.SEARCH} element={<PublicLayout><Search /></PublicLayout>} />
                <Route path={routes.SCHEDULES} element={<PublicLayout><SchedulesPage /></PublicLayout>} />
                <Route path={routes.EVENTS} element={<PublicLayout><EventsHub /></PublicLayout>} />
                <Route path={routes.TENDERS} element={<PublicLayout><TendersPage /></PublicLayout>} />

                {/* ================= FACULTIES ================= */}
                <Route path={routes.FACULTIES} element={<PublicLayout><Faculties /></PublicLayout>} />
                <Route path={`${routes.FACULTIES}/:slug`} element={<PublicLayout><FacultyDetail /></PublicLayout>} />

                {/* ================= CENTERS ================= */}
                <Route path={routes.CENTRES} element={<PublicLayout><Centers /></PublicLayout>} />
                <Route path={`${routes.CENTRES}/:center`} element={<PublicLayout><Center /></PublicLayout>} />

                {/* ================= ARTICLES ================= */}
                <Route path={routes.ARTICLES} element={<PublicLayout><Articles /></PublicLayout>} />
                <Route path={`${routes.ARTICLES}/:category?`} element={<PublicLayout><Articles /></PublicLayout>} />
                <Route path={`${routes.ARTICLESDETAILS}/:article`} element={<PublicLayout><ArticleDerails /></PublicLayout>} />

                {/* ================= SPECIAL PROGRAMS ================= */}
                <Route path={`${routes.SPECIAL_PROGRAMS}/:slug`} element={<PublicLayout><SpecialProgramDetail /></PublicLayout>} />

                {/* ================= EXTRA ================= */}
                <Route path={routes.ADMISSION} element={<PublicLayout><Domaines /></PublicLayout>} />
                <Route path={routes.RESEARCH} element={<PublicLayout><Domaines /></PublicLayout>} />
                <Route path={routes.ACTIVITIES} element={<PublicLayout><Domaines /></PublicLayout>} />
                <Route path={routes.RESSOURCES} element={<PublicLayout><Domaines /></PublicLayout>} />
                <Route path={routes.BIBLIO} element={<PublicLayout><Library /></PublicLayout>} />
                <Route path={routes.KAUTA} element={<PublicLayout><Kauta /></PublicLayout>} />
                <Route path={routes.METANOIA} element={<PublicLayout><Metanoia /></PublicLayout>} />
                <Route path={routes.AUMONERIE} element={<PublicLayout><Aumonerie /></PublicLayout>} />

                {/* ================= AUTH (NO LAYOUT) ================= */}
                <Route path={routes.LOGIN} element={<Login />} />
                <Route path={routes.FORGOT_PASSWORD} element={<ForgotPassword />} />
                <Route path={routes.RESET_PASSWORD} element={<ResetPassword />} />

                {/* ================= ADMIN ================= */}
                <Route path={routes.ADMIN} element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="articles" element={<AdminDashboard />} />
                  <Route path="events" element={<AdminDashboard />} />
                  <Route path="activities" element={<AdminDashboard />} />
                  <Route path="users" element={<AdminDashboard />} />
                  <Route path="centers" element={<AdminDashboard />} />
                  <Route path="faqs" element={<AdminDashboard />} />
                  <Route path="key-facts" element={<AdminDashboard />} />
                  <Route path="partners" element={<AdminDashboard />} />
                  <Route path="admission-guide" element={<AdminDashboard />} />
                  <Route path="academic-fees" element={<AdminDashboard />} />
                  <Route path="schedules" element={<AdminDashboard />} />
                  <Route path="tenders" element={<AdminDashboard />} />
                  <Route path="faculties" element={<AdminDashboard />} />
                  <Route path="staff" element={<AdminDashboard />} />
                  <Route path="special-programs" element={<AdminDashboard />} />
                  <Route path="visits" element={<AdminDashboard />} />
                  <Route path="login-history" element={<AdminDashboard />} />
                  <Route path="messages" element={<AdminDashboard />} />
                  <Route path="pending" element={<AdminDashboard />} />
                  <Route path="newsletter" element={<AdminDashboard />} />
                </Route>

                {/* ================= 404 ================= */}
                <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />

              </Routes>

            </Router>
            <ToastContainer
              position="top-center"
              autoClose={5000}
              hideProgressBar={true}
              newestOnTop={false}
              closeOnClick={false}
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </IsMobileProvider>
        </LanguageProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}